import { Badge } from "@/components/ui/badge";
import { KnowledgeEntry } from "../types";
import { File, CheckCircle2, AlertCircle, Clock, Database, FileText, Tag } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface MetadataStatusBadgesProps {
  entry: KnowledgeEntry;
}

export function MetadataStatusBadges({ entry }: MetadataStatusBadgesProps) {
  // Check if the entry has been stored in OpenAI vector store
  const isStoredInVector = entry.metadata?.vector_store_id !== undefined && entry.metadata?.openai_file_id !== undefined;
  
  // Check if metadata has been processed - verify actual presence of data, not just presence of properties
  const hasGeneratedTitle = !!entry.metadata?.generated_title;
  const hasGeneratedTags = entry.metadata?.generated_tags && 
    (Array.isArray(entry.metadata.generated_tags) ? 
      entry.metadata.generated_tags.length > 0 : 
      !!entry.metadata.generated_tags);
  const hasSummary = !!entry.metadata?.summary;
  
  // Check specific metadata processing status
  const isMetadataProcessed = hasGeneratedTitle || hasGeneratedTags || hasSummary;
  
  // Check overall process status
  const isFullyProcessed = isStoredInVector && isMetadataProcessed;
  
  // Check if there were any errors
  const hasErrors = !!entry.embedding_error || !!entry.metadata?.processing_error;

  // Check if OpenAI API is currently having issues with this entry
  const hasOpenAIApiError = 
    entry.metadata?.processing_error && 
    (typeof entry.metadata.processing_error === 'string' ? 
      entry.metadata.processing_error.includes('OpenAI API error') : 
      JSON.stringify(entry.metadata.processing_error).includes('OpenAI API error'));

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {/* File upload status */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant={entry.file_url ? "default" : "outline"}
              className="flex items-center gap-1"
            >
              <File className="h-3 w-3" />
              {entry.file_url ? "File Uploaded" : "No File"}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>{entry.file_url ? "A file has been uploaded to storage" : "No file has been uploaded yet"}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {/* Vector storage status */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant={isStoredInVector ? "default" : "outline"}
              className={isStoredInVector ? "bg-blue-500 hover:bg-blue-600 flex items-center gap-1" : "flex items-center gap-1"}
            >
              <Database className="h-3 w-3" />
              {isStoredInVector ? "Stored in Vector" : "Not Vectorized"}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {isStoredInVector 
                ? `File is stored in OpenAI vector store (${entry.metadata?.vector_store_id || 'unknown ID'})` 
                : "File has not been stored in a vector database yet"}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {/* Title generation status */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <div>
              <Badge 
                variant={hasGeneratedTitle ? "default" : "outline"}
                className={hasGeneratedTitle ? "bg-green-500 hover:bg-green-600 flex items-center gap-1" : "flex items-center gap-1"}
              >
                <FileText className="h-3 w-3" />
                {hasGeneratedTitle ? "Title Generated" : "No Title"}
              </Badge>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {hasGeneratedTitle 
                ? `AI-generated title: "${entry.metadata.generated_title}"` 
                : "No AI-generated title available"}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {/* Tags generation status */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <div>
              <Badge 
                variant={hasGeneratedTags ? "default" : "outline"}
                className={hasGeneratedTags ? "bg-purple-500 hover:bg-purple-600 flex items-center gap-1" : "flex items-center gap-1"}
              >
                <Tag className="h-3 w-3" />
                {hasGeneratedTags ? "Tags Generated" : "No Tags"}
              </Badge>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {hasGeneratedTags 
                ? `AI-generated tags available` 
                : "No AI-generated tags available"}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {/* Metadata processing status */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <div>
              <Badge 
                variant={isMetadataProcessed ? "default" : "outline"}
                className={isMetadataProcessed ? "bg-purple-500 hover:bg-purple-600 flex items-center gap-1" : "flex items-center gap-1"}
              >
                <Clock className="h-3 w-3" />
                {isMetadataProcessed ? "Metadata Processed" : "Awaiting Processing"}
              </Badge>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {isMetadataProcessed 
                ? "Document has been processed for additional metadata" 
                : hasOpenAIApiError 
                  ? "OpenAI API error during metadata processing" 
                  : "Document is waiting to be processed for metadata extraction"}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {/* Overall status */}
      {(isFullyProcessed || hasErrors) && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge 
                variant={hasErrors ? "destructive" : "default"}
                className={hasErrors ? "flex items-center gap-1" : "bg-green-500 hover:bg-green-600 flex items-center gap-1"}
              >
                {hasErrors ? (
                  <>
                    <AlertCircle className="h-3 w-3" />
                    Error
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-3 w-3" />
                    Completed
                  </>
                )}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {hasErrors 
                  ? `Error: ${entry.embedding_error || (entry.metadata?.processing_error 
                      ? (typeof entry.metadata.processing_error === 'string' 
                        ? entry.metadata.processing_error 
                        : 'Processing error (see details below)') 
                      : 'Unknown error')}`
                  : "All processing steps have completed successfully"}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
